<template>
  <UserListCard
    v-if="canView"
    :title="$t('label.dashboardNewReports')"
    :widget="widget"
    :view-all-to="{ name: 'Report' }"
    @retry="loadReports()"
    @page="loadReports($event)"
  >
    <b-table
      :items="widget.reports"
      :fields="fields"
      primary-key="uuid"
      responsive
      show-empty
    >
      <template #cell(description)="{ item }">
        <details
          v-if="item.description.length > 120"
          :key="item.uuid"
          class="report-description report-description-details"
        >
          <summary>
            <span class="report-description-preview"
              >{{ item.description.slice(0, 120) }}…
              <span class="text-info">{{
                $t("label.dashboardShowMore")
              }}</span></span
            >
            <span class="report-description-less text-info">{{
              $t("label.dashboardShowLess")
            }}</span>
          </summary>
          <div class="mt-2">{{ item.description }}</div>
        </details>
        <div v-else class="report-description">{{ item.description }}</div>
      </template>
      <template #cell(type)="{ item }">{{
        optionLabel(typeOptions, item.type)
      }}</template>
      <template #cell(platformType)="{ item }">{{
        optionLabel(platformOptions, item.platformType)
      }}</template>
    </b-table>
  </UserListCard>
</template>
<script>
import UserListCard from "./UserListCard.vue";
import { dashboardService } from "@/services/api/dashboard.service";
import moment from "moment";
import { reportTypeOptions, reportPlatformOptions } from "@/constants/report";
import { sessionHelper } from "@/utils";

export default {
  name: "DashboardNewReports",
  components: { UserListCard },
  props: { permission: { type: Number, default: 0 } },
  data() {
    return {
      widget: {
        reports: [],
        total: 0,
        page: 1,
        pageSize: 15,
        loading: true,
        error: false,
      },
      requestId: 0,
    };
  },
  computed: {
    canView() {
      return this.permission >= 1;
    },
    typeOptions() {
      return reportTypeOptions();
    },
    platformOptions() {
      return reportPlatformOptions();
    },
    fields() {
      return [
        { key: "name", label: this.$t("label.name") },
        { key: "email", label: this.$t("label.email") },
        { key: "type", label: this.$t("label.type") },
        { key: "description", label: this.$t("label.description") },
        {
          key: "platformType",
          label: this.$t("label.dashboardReportPlatform"),
        },
        {
          key: "createdAt",
          label: this.$t("label.createdAt"),
          class: "text-nowrap",
        },
      ];
    },
  },
  watch: {
    canView() {
      this.loadReports(1);
    },
  },
  created() {
    this.loadReports();
  },
  beforeDestroy() {
    this.requestId++;
  },
  methods: {
    optionLabel(options, value) {
      return options.find((option) => option.value === value)?.text || "-";
    },
    async loadReports(page = this.widget.page) {
      const requestId = ++this.requestId;
      const widget = this.widget;
      if (!this.canView || sessionHelper.getPermissionLevel("developer") < 1) {
        widget.reports = [];
        widget.total = 0;
        widget.page = 1;
        widget.loading = false;
        widget.error = false;
        return;
      }
      widget.loading = true;
      widget.error = false;
      try {
        const result = await dashboardService.getNewReports(
          page,
          widget.pageSize,
        );
        if (!result?.success || !Array.isArray(result.data))
          throw new Error("Invalid new report list.");
        if (requestId !== this.requestId) return;
        widget.reports = result.data.map((report) => {
          const user = report.user;
          const created = moment(report.created_at, moment.ISO_8601, true);
          return {
            uuid: report.uuid,
            name:
              user?.name ||
              [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
              "-",
            email: user?.email || "-",
            type: report.type == null ? null : Number(report.type),
            description: report.description || "-",
            platformType:
              report.platform_type == null
                ? null
                : Number(report.platform_type),
            createdAt:
              report.created_at && created.isValid()
                ? created.utcOffset(480).format("YYYY-MM-DD HH:mm:ss")
                : "-",
          };
        });
        widget.total = result.pagination?.total ?? result.data.length;
        widget.page = result.pagination?.current_page || page;
        widget.pageSize = result.pagination?.per_page || widget.pageSize;
      } catch (error) {
        if (requestId === this.requestId) widget.error = true;
      } finally {
        if (requestId === this.requestId) widget.loading = false;
      }
    },
  },
};
</script>
<style scoped>
.report-description {
  min-width: 200px;
  max-width: 400px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.report-description-details summary {
  cursor: pointer;
}
.report-description-less {
  display: none;
}
.report-description-details[open] .report-description-preview {
  display: none;
}
.report-description-details[open] .report-description-less {
  display: inline;
}
</style>
